/* eslint-disable react/prop-types */
/**
 * UserProvider
 * Contain most logic handing with Omnitech Api
 */
import _ from 'lodash'
import React, { useEffect, useReducer } from 'react'

import { useAuth } from '../../auth'
import { useSystemSettings } from '../../system-settings'

import { fetchStoredValueTransactions } from '../../stored-value-transactions/shared/stored-value-transactions-api'
import {
  fetchUser,
  fetchUserLoyaltyInformation,
  fetchUserStoredValue,
  updateUser,
  deleteUser,
  createUserResetPasswordRequest,
  createUserResetPassword,
  createUsersOmniAuthOtps
} from './user-api'
import UserContext from './user-context'
import userReducer from './user-reducer'
import { cancelRequest } from '../../helpers'

export default function UserProvider({ children }) {
  const { auth = {}, setAuth } = useAuth()
  const {
    getSystemSetting,
    systemSettingsReady,
  } = useSystemSettings()
  const { userId } = auth
  const [state, dispatch] = useReducer(userReducer, {
    countries: [],
    countriesReady: false,
    loyaltyInformation: {},
    mostRecentStoredValueTransaction: {},
    storedValueBalances: [],
    user: {},
  })

  const setUser = (user) => {
    dispatch({ type: 'SET_USER', payload: { user } })
  }

  const setUserReady = (userReady) => {
    dispatch({ type: 'SET_USER_READY', payload: { userReady } })
  }

  const setLoyaltyInformation = (loyaltyInformation) => {
    dispatch({ type: 'SET_LOYALTY_INFORMATION', payload: { loyaltyInformation } })
  }

  const setStoredValueBalances = (storedValueBalances) => {
    dispatch({ type: 'SET_STORED_VALUE_BALANCES', payload: { storedValueBalances } })
  }

  const setMostRecentStoredValueTransaction = (mostRecentStoredValueTransaction) => {
    dispatch({ type: 'SET_MOST_RECENT_STORED_VALUE_TRANSACTION', payload: { mostRecentStoredValueTransaction } })
  }

  function resetUser() {
    setUser({})
  }

  const enableStoredValue = getSystemSetting('payment_gateway.enable_stored_value', false)

  function fetchUserInfo() {
    const promises = [fetchUserLoyaltyInformation(userId)]
    if (enableStoredValue) {
      promises.push(fetchUserStoredValue(userId))
      // get user most recent expiry data for stored value
      promises.push(fetchStoredValueTransactions({
        scopes: [
          'unexpired', // All unexpired value
          'unspent',
        ],
        sortBy: 'expire_at',
        pageSize: 1,
      }))
    }
    return Promise.all(promises)
      .then((values) => {
        _.each(
          values,
          (value) => {
            const userLoyaltyInformation = _.get(value, 'loyaltyInformation')
            const userStoredValueBalances = _.get(value, 'storedValueBalances')
            const userMostRecentStoredValueTransactions = _.get(value, 'storedValueTransactions')
            if (userLoyaltyInformation) {
              setLoyaltyInformation(userLoyaltyInformation)
            }
            if (userStoredValueBalances) {
              setStoredValueBalances(userStoredValueBalances)
            }
            if (userMostRecentStoredValueTransactions) {
              setMostRecentStoredValueTransaction(_.first(userMostRecentStoredValueTransactions))
            }
          }
        )
      })

  }

  async function handleFetchUser() {
    try {
      const { user: data } = await fetchUser(userId)
      setUser(data)
      await fetchUserInfo()
    } catch (error) {
      const generalError = _.get(error, 'generalError', {})
      // if 401, reset auth token, udid and user id when fail to get user from API
      if (generalError.code === 401) {
        setAuth({
          ...auth,
          authToken: '',
          userId: ''
        })
      }
      throw error
    }
  }

  async function handleUpdateUser(user) {
    try {
      const { user: updatedUser } = await updateUser(user)
      setUser(updatedUser)
      await fetchUserInfo()
    } catch (error) {
      const generalError = _.get(error, 'generalError', {})
      // if 401, reset auth token, udid and user id when fail to get user from API
      if (generalError.code === 401) {
        setAuth({
          ...auth,
          authToken: '',
          userId: ''
        })
      } else {
        console.warn('error: ', error)
        throw error
      }
    }
  }

  useEffect(() => {
    // only fetch user when user data is existed
    if (!userId) {
      setUserReady(true)
      return
    }

    // wait for system settings api call
    if (
      !systemSettingsReady
    ) return

    try {
      handleFetchUser()
    } catch (error) {
      throw error
    } finally {
      setUserReady(true)
    }
    return function cleanUp() {
      cancelRequest.cancelAll(['fetchUser'])
    }
  }, [userId, systemSettingsReady])

  return (
    <UserContext.Provider value={{
      user: state.user,
      userReady: state.userReady,
      loyaltyInformation: state.loyaltyInformation,
      mostRecentStoredValueTransaction: state.mostRecentStoredValueTransaction,
      storedValueBalances: state.storedValueBalances,
      fetchUser: handleFetchUser,
      fetchUserLoyaltyInformation,
      fetchUserStoredValue,
      resetUser,
      updateUser: handleUpdateUser,
      deleteUser,
      createUserResetPasswordRequest,
      createUserResetPassword,
      createUsersOmniAuthOtps
    }}>
      {children}
    </UserContext.Provider>
  )
}
